import { HttpErrorResponse } from '@angular/common/http';

/**
 * PII-safe error summary for console logging — the frontend equivalent of
 * ADR-0014 §4 ("PHI never enters logs"), which the backend enforces via
 * `libs/back/api/common-api/src/lib/logger/redact-pii.ts` (#1603). There was
 * no shared frontend counterpart until this util (#2133): the same shape had
 * been reinvented ad-hoc three times (`AdminAuthService.debug`'s `isDevMode()`
 * gate, `GlobalErrorHandler.logInDev`, and a private `safeErrorSummary` on
 * `AdminAuthService`, #2131 item 4 / #2132).
 *
 * Raw error objects must NEVER be passed to `console.error`/`console.warn`
 * directly:
 *  - an `HttpErrorResponse` embeds the full request URL on both `.url` and
 *    inside `.message` — request paths routinely carry PII (e.g. an
 *    admin-lookup-by-email endpoint embeds the admin's own email address);
 *  - other error shapes (JWT-decode failures, Auth0 SDK errors, storage
 *    errors) can carry decoded token claims or other sensitive detail
 *    embedded in nested fields that a naive `console.error(error)` would
 *    serialize in full.
 *
 * Use this at every call site that logs a caught `error` to the console.
 *
 * Keeps what IS useful for debugging — the HTTP status code + status text
 * for transport errors, and the error name + message for everything else —
 * while deliberately never inspecting `.url`, decoding token payloads, or
 * stringifying the full object. Only these three primitive fields are ever
 * returned; the underlying error is never passed through.
 */
export function safeErrorSummary(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return `HTTP ${error.status} ${error.statusText}`;
  }
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return `non-Error value of type ${typeof error}`;
}
